/**
 * Utilidades para normalización de marcas
 * Mantiene la consistencia entre frontend y backend
 */

/**
 * Normaliza el nombre de una marca para evitar inconsistencias
 * @param {string} name - Nombre de la marca a normalizar
 * @returns {string} - Nombre normalizado
 */
export const normalizeBrandName = (name) => {
  if (!name) return '';
  
  // Convertir a string y limpiar espacios
  let normalized = String(name).trim();
  
  // Normalizar espacios múltiples
  normalized = normalized.replace(/\s+/g, ' ');
  
  // Normalizar caracteres especiales comunes
  const replacements = {
    '&': '&',
    '＆': '&',
    'and': '&',
    'And': '&',
  };
  
  for (const [old, newChar] of Object.entries(replacements)) {
    normalized = normalized.replace(new RegExp(old, 'gi'), newChar);
  }
  
  // Aplicar capitalización consistente
  const words = normalized.split(' ');
  const normalizedWords = words.map(word => {
    if (word === '&') return '&';
    if (/^[a-zA-Z]/.test(word)) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word;
  });
  
  return normalizedWords.join(' ');
};

/**
 * Genera un brand_id a partir del nombre de marca
 * @param {string} brandName - Nombre de la marca
 * @returns {string} - ID de marca en formato kebab-case
 */
export const generateBrandId = (brandName) => {
  if (!brandName) return '';
  
  const normalized = normalizeBrandName(brandName);
  return normalized.toLowerCase().replace(/\s+/g, '-');
};

/**
 * Compara dos nombres de marca de forma normalizada
 * @param {string} brand1 - Primera marca
 * @param {string} brand2 - Segunda marca
 * @returns {boolean} - true si son la misma marca normalizada
 */
export const compareBrands = (brand1, brand2) => {
  return normalizeBrandName(brand1) === normalizeBrandName(brand2);
};

/**
 * Busca una marca en una lista usando normalización
 * @param {Array} brandList - Lista de marcas
 * @param {string} searchBrand - Marca a buscar
 * @param {string} brandField - Campo que contiene el nombre de la marca (default: 'brand_name')
 * @returns {Object|null} - Marca encontrada o null
 */
export const findBrandNormalized = (brandList, searchBrand, brandField = 'brand_name') => {
  const normalizedSearch = normalizeBrandName(searchBrand);
  
  return brandList.find(brand => {
    const brandName = brand[brandField] || brand.brand || brand.name;
    return normalizeBrandName(brandName) === normalizedSearch;
  }) || null;
};

/**
 * Obtiene una lista de marcas únicas normalizadas
 * @param {Array} items - Lista de items que contienen marcas
 * @param {string} brandField - Campo que contiene el nombre de la marca
 * @returns {Array} - Lista de marcas únicas normalizadas
 */
export const getUniqueNormalizedBrands = (items, brandField = 'brand') => {
  const brandSet = new Set();
  
  items.forEach(item => {
    const brandName = item[brandField];
    if (brandName) {
      const normalized = normalizeBrandName(brandName);
      brandSet.add(normalized);
    }
  });
  
  return Array.from(brandSet).sort();
};

export default {
  normalizeBrandName,
  generateBrandId,
  compareBrands,
  findBrandNormalized,
  getUniqueNormalizedBrands
};
